import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { House, ArrowLeft, Printer, User } from "@phosphor-icons/react";

export default function NewHomeInterventionPage() {
  useDocumentTitle("Nuovo intervento a domicilio");
  const nav = useNavigate();
  const [saving, setSaving] = useState(false);
  const [existingCustomers, setExistingCustomers] = useState([]);
  const [customerMode, setCustomerMode] = useState("new");
  const [customerId, setCustomerId] = useState("");
  const [customerQuery, setCustomerQuery] = useState("");

  const [form, setForm] = useState({
    name: "", surname: "", phone: "", email: "", address: "",
    work_description: "",
    tech_notes: "",
    price_estimate: "",
    cost: "",
  });
  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  useEffect(() => {
    if (customerMode !== "existing") return;
    const t = setTimeout(async () => {
      const { data } = await api.get("/customers", { params: customerQuery ? { q: customerQuery } : {} });
      setExistingCustomers(data);
    }, 200);
    return () => clearTimeout(t);
  }, [customerQuery, customerMode]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.work_description.trim()) return toast.error("Descrizione lavoro obbligatoria");
    if (customerMode === "new" && (!form.name.trim() || !form.surname.trim() || !form.phone.trim())) {
      return toast.error("Nome, cognome e telefono cliente obbligatori");
    }
    if (customerMode === "existing" && !customerId) return toast.error("Seleziona un cliente");

    setSaving(true);
    try {
      const payload = {
        intervention_type: "home",
        work_description: form.work_description,
        tech_notes: form.tech_notes || null,
        price_estimate: form.price_estimate ? parseFloat(form.price_estimate) : null,
        cost: form.cost ? parseFloat(form.cost) : null,
      };
      if (customerMode === "existing") {
        payload.customer_id = customerId;
      } else {
        payload.customer = {
          name: form.name.trim(),
          surname: form.surname.trim(),
          phone: form.phone.trim(),
          email: form.email || null,
          address: form.address || null,
        };
      }
      const { data } = await api.post("/interventions", payload);
      toast.success(`Intervento ${data.code} registrato`);
      // Redirect directly to receipt (auto-print)
      nav(`/interventi/${data.id}/scontrino`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto">
      <button onClick={() => nav(-1)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4"
              style={{ transition: "color 0.15s ease" }}>
        <ArrowLeft size={14} /> Indietro
      </button>
      <div className="mb-8 flex items-center gap-3">
        <div className="h-12 w-12 rounded-md bg-primary/10 text-primary grid place-items-center">
          <House size={22} weight="duotone" />
        </div>
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">// DOMICILIO</div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Nuovo intervento a domicilio</h1>
          <p className="text-muted-foreground text-sm mt-1">Al salvataggio verrà stampato lo scontrino 58mm.</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="new-home-form">
        <Card className="p-6 border-border/70 rounded-lg">
          <div className="flex items-center gap-2 mb-4">
            <User size={18} weight="duotone" className="text-primary" />
            <h2 className="font-display text-lg font-bold tracking-tight">Cliente</h2>
          </div>
          <div className="flex gap-1 bg-secondary rounded-md p-1 w-fit mb-5">
            {[
              { k: "new", l: "Nuovo cliente" },
              { k: "existing", l: "Cliente esistente" },
            ].map((t) => (
              <button
                key={t.k}
                type="button"
                onClick={() => setCustomerMode(t.k)}
                data-testid={`home-customer-mode-${t.k}`}
                className={`px-4 py-1.5 rounded text-sm font-medium ${customerMode === t.k ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}
              >{t.l}</button>
            ))}
          </div>
          {customerMode === "new" ? (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Cognome *"><Input required value={form.surname} onChange={on("surname")} data-testid="home-customer-surname" /></Field>
              <Field label="Nome *"><Input required value={form.name} onChange={on("name")} data-testid="home-customer-name" /></Field>
              <Field label="Telefono *"><Input required value={form.phone} onChange={on("phone")} data-testid="home-customer-phone" /></Field>
              <Field label="Email"><Input type="email" value={form.email} onChange={on("email")} data-testid="home-customer-email" /></Field>
              <div className="sm:col-span-2"><Field label="Indirizzo"><Input value={form.address} onChange={on("address")} data-testid="home-customer-address" /></Field></div>
            </div>
          ) : (
            <div className="space-y-3">
              <Input placeholder="Cerca cliente…" value={customerQuery} onChange={(e) => setCustomerQuery(e.target.value)} data-testid="home-existing-search" />
              <div className="border border-border/60 rounded-md max-h-64 overflow-y-auto divide-y divide-border/50">
                {existingCustomers.length === 0 && (
                  <div className="p-4 text-sm text-muted-foreground text-center">Nessun cliente trovato</div>
                )}
                {existingCustomers.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => setCustomerId(c.id)}
                    data-testid={`home-existing-${c.id}`}
                    className={`w-full text-left p-3 hover:bg-secondary/60 flex justify-between items-center ${customerId === c.id ? "bg-primary/10" : ""}`}
                    style={{ transition: "background-color 0.15s ease" }}
                  >
                    <div>
                      <div className="font-medium">{c.surname} {c.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{c.phone}</div>
                    </div>
                    {customerId === c.id && <span className="font-mono text-xs text-primary">SELEZIONATO</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Card>

        <Card className="p-6 border-border/70 rounded-lg">
          <h2 className="font-display text-lg font-bold tracking-tight mb-4">Intervento eseguito</h2>
          <div className="space-y-4">
            <Field label="Descrizione lavoro *">
              <Textarea rows={4} required value={form.work_description} onChange={on("work_description")} data-testid="home-work-description"
                        placeholder="Es. Installazione stampante, configurazione WiFi, rimozione virus..." />
            </Field>
            <Field label="Note tecniche">
              <Textarea rows={2} value={form.tech_notes} onChange={on("tech_notes")} data-testid="home-tech-notes" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Importo — quanto paga il cliente (€)">
                <Input type="number" step="0.01" min="0" value={form.price_estimate} onChange={on("price_estimate")} data-testid="home-price" className="font-mono" placeholder="Opzionale" />
              </Field>
              <Field label="Costo — quanto hai speso (€)">
                <Input type="number" step="0.01" min="0" value={form.cost} onChange={on("cost")} data-testid="home-cost" className="font-mono" placeholder="Opzionale" />
              </Field>
            </div>
          </div>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => nav(-1)} className="rounded-full">Annulla</Button>
          <Button type="submit" disabled={saving} data-testid="save-home-btn" className="rounded-full gap-2">
            <Printer size={16} weight="bold" />
            {saving ? "Salvataggio…" : "Salva e stampa scontrino"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
